import React from "react";
import { Box, Skeleton, Paper } from "@mui/material";
import PageContainer from "./PageContainer";
import PageSection from "./PageSection";

const CalendarSkeleton: React.FC = () => {
  return (
    <PageContainer sx={{ mt: 2 }}>
      {/* Search Section Skeleton */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            height: 48,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            px: 2,
            gap: 1,
          }}
        >
          {/* Search icon skeleton */}
          <Skeleton variant="circular" width={20} height={20} />
          {/* Search text skeleton */}
          <Skeleton variant="text" width="25%" height={20} />
        </Box>
      </Box>

      <PageSection>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 350px" },
            gap: 3,
            alignItems: "start",
          }}
        >
          {/* Main Calendar Skeleton */}
          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              p: 3,
            }}
          >
            {/* Calendar Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
                gap: 2,
              }}
            >
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>

            {/* Calendar Grid */}
            <Box sx={{ mb: 2 }}>
              {/* Day headers */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 1,
                  mb: 2,
                }}
              >
                {Array.from({ length: 7 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="text"
                    height={24}
                    sx={{ textAlign: "center" }}
                  />
                ))}
              </Box>

              {/* Calendar days */}
              {Array.from({ length: 6 }).map((_, weekIndex) => (
                <Box
                  key={weekIndex}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  {Array.from({ length: 7 }).map((_, dayIndex) => (
                    <Box
                      key={dayIndex}
                      sx={{
                        aspectRatio: "1",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        p: 1,
                        minHeight: 80,
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width={20}
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      {/* Randomly show some event skeletons */}
                      {Math.random() > 0.7 && (
                        <Skeleton
                          variant="rounded"
                          width="80%"
                          height={16}
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        />
                      )}
                      {Math.random() > 0.85 && (
                        <Skeleton
                          variant="rounded"
                          width="90%"
                          height={16}
                          sx={{ borderRadius: 1 }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Events Sidebar Skeleton */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Paper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 3,
              }}
            >
              {/* Sidebar title */}
              <Skeleton variant="text" width="60%" height={32} sx={{ mb: 3 }} />

              {/* Summary text */}
              <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="70%" height={20} sx={{ mb: 3 }} />

              {/* Event list */}
              {Array.from({ length: 5 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  {/* Event date square */}
                  <Skeleton
                    variant="rounded"
                    width={40}
                    height={40}
                    sx={{ borderRadius: 1, mr: 2 }}
                  />

                  {/* Event content */}
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={20}
                      sx={{ mb: 0.5 }}
                    />
                    <Skeleton variant="text" width="60%" height={16} />
                  </Box>
                </Box>
              ))}

              {/* Month separator */}
              <Box sx={{ textAlign: "center", my: 2 }}>
                <Skeleton
                  variant="text"
                  width="40%"
                  height={16}
                  sx={{ mx: "auto" }}
                />
              </Box>

              {/* More events */}
              {Array.from({ length: 3 }).map((_, index) => (
                <Box
                  key={index + 5}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={40}
                    height={40}
                    sx={{ borderRadius: 1, mr: 2 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width="75%"
                      height={20}
                      sx={{ mb: 0.5 }}
                    />
                    <Skeleton variant="text" width="55%" height={16} />
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>
        </Box>
      </PageSection>
    </PageContainer>
  );
};

export default CalendarSkeleton;
